import React, { useEffect, useMemo, useRef, useState, useCallback, useId } from 'react';
import ReactDOM from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';
import { controlClass, Spinner } from '../ui';

export interface Option {
  label: string;
  value: string;
  [key: string]: any; // Allow extra props
}

interface AsyncSelectProps {
  value?: Option | null;
  onChange: (option: Option | null) => void;
  loadOptions: (input: string, page: number) => Promise<{ options: Option[]; hasMore: boolean }>;
  placeholder?: string;
  allowClear?: boolean;
  className?: string;
  disabled?: boolean;
  customFooter?: React.ReactNode;
}

export const AsyncSelect: React.FC<AsyncSelectProps> = ({
  value,
  onChange,
  loadOptions,
  placeholder = 'Select...',
  allowClear = true,
  className = '',
  disabled = false,
  customFooter,
}) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastOptionRef = useCallback((node: HTMLButtonElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // Close on outside click — check both container and portal dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inDropdown = listRef.current?.contains(target);
      if (!inContainer && !inDropdown) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Position the portal dropdown relative to the input
  const updatePosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownMaxH = 256; // max-h-64 = 16rem = 256px
    const openAbove = spaceBelow < dropdownMaxH && rect.top > spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, []);

  // Update position when open or on scroll/resize
  useEffect(() => {
    if (!open) return;
    updatePosition();

    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updatePosition]);

  // Debounced search / load
  useEffect(() => {
    if (!open) return;

    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { options: newOptions, hasMore: more } = await loadOptions(input, page);

        if (active) {
          if (page === 1) {
            setOptions(newOptions);
          } else {
            setOptions(prev => [...prev, ...newOptions]);
          }
          setHasMore(more);
        }
      } catch (err) {
        console.error("AsyncSelect load failed", err);
        if (active) {
          setHasMore(false);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);

    return () => { active = false; clearTimeout(timer); };
  }, [input, page, open, loadOptions]);

  // Reset page when input changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    if (open) setOptions([]);
  }, [input, open]);

  const display = useMemo(() => value?.label || '', [value]);
  const listboxId = useId();

  const dropdown = open ? ReactDOM.createPortal(
    <div
      ref={listRef}
      className="flex max-h-64 animate-fade-up flex-col overflow-hidden rounded-2xl bg-white font-ui shadow-[0_16px_40px_-12px_rgba(0,0,0,0.2)] ring-1 ring-black/[0.06]"
      style={dropdownStyle}
    >
      <div id={listboxId} role="listbox" className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5">
        {options.length === 0 && !loading ? (
          <div className="px-3 py-2 text-[13px] text-neutral-400">No results</div>
        ) : (
          <>
            {options.map((opt, index) => {
              const isLast = index === options.length - 1;
              const selected = value?.value === opt.value;
              return (
                <button
                  ref={isLast ? lastOptionRef : null}
                  key={`${opt.value}-${index}`}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => { onChange(opt); setOpen(false); }}
                  className={`flex min-h-[40px] w-full flex-shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-left text-[13px] text-ink transition-colors hover:bg-neutral-100 focus-visible:bg-neutral-100 focus-visible:outline-none sm:min-h-[36px] sm:py-1.5 ${selected ? 'font-medium' : ''}`}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {opt.label}
                    {opt.extraLabel && <span className="ml-2 text-[12px] font-normal text-neutral-400">{opt.extraLabel}</span>}
                  </span>
                  {selected && <Check className="h-4 w-4 flex-shrink-0 text-primary-600" strokeWidth={2} />}
                </button>
              );
            })}
          </>
        )}

        {loading && (
          <div className="flex flex-shrink-0 items-center justify-center gap-2 px-3 py-2 text-[12px] text-neutral-400">
            <Spinner className="h-3 w-3" />
            Loading
          </div>
        )}
      </div>

      {customFooter && (
        <div className="flex-shrink-0 border-t border-neutral-200/70 bg-white p-1.5">
          {customFooter}
        </div>
      )}
    </div>,
    document.body
  ) : null;

  const showClear = allowClear && value && !disabled;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        className={`${controlClass} h-10 pl-3 pr-9 sm:h-9`}
        placeholder={placeholder}
        value={open ? input : display}
        onFocus={() => {
          if (!disabled) {
            setOpen(true);
            setInput('');
          }
        }}
        onChange={(e) => setInput(e.target.value)}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        autoComplete="off"
      />
      {showClear ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange(null);
            setInput('');
          }}
          className="absolute right-1.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-ink"
          title="Clear"
          aria-label="Clear"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      ) : (
        <ChevronDown
          className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
          strokeWidth={1.75}
          aria-hidden="true"
        />
      )}
      {dropdown}
    </div>
  );
};
